export const SALES_IMPORT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const SALES_IMPORT_XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function asNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function asNullableNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function errorPayload(error) {
  return error?.response?.data || error?.data || {};
}

export function validateSalesImportFile(file) {
  if (!file) return "Seleccioná un archivo para continuar.";
  if (!String(file.name || "").toLowerCase().endsWith(".xlsx")) {
    return "El archivo debe tener formato .xlsx.";
  }
  if (asNumber(file.size) <= 0) return "El archivo está vacío.";
  if (asNumber(file.size) > SALES_IMPORT_MAX_FILE_SIZE_BYTES) {
    return "El archivo supera el tamaño máximo permitido de 10 MB.";
  }
  return "";
}

export function formatSalesImportFileSize(value) {
  const bytes = asNumber(value);
  if (bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) return `${kilobytes.toLocaleString("es-AR", {
    maximumFractionDigits: 1,
  })} KB`;
  return `${(kilobytes / 1024).toLocaleString("es-AR", {
    maximumFractionDigits: 1,
  })} MB`;
}

export function normalizeSalesImportDraft(response = {}) {
  return {
    id: asNullableNumber(response.id),
    status: String(response.status || ""),
    version: asNumber(response.version, 1),
    expiresAt: response.expiresAt || null,
    salesDateFrom: String(response.salesDateFrom || ""),
    salesDateTo: String(response.salesDateTo || ""),
    proposedMovementDate: String(response.proposedMovementDate || ""),
    processedRows: asNumber(response.processedRows),
    totalUnitsSold: String(response.totalUnitsSold ?? "0"),
    distinctReceiptCount: asNumber(response.distinctReceiptCount),
    distinctProductCount: asNumber(response.distinctProductCount),
    totalSalesAmount: String(response.totalSalesAmount ?? "0"),
    pointsOfSale: Array.isArray(response.pointsOfSale)
      ? response.pointsOfSale.map(String)
      : [],
    file: {
      originalName: String(response.file?.originalName || ""),
      mimeType: String(response.file?.mimeType || ""),
      size: asNumber(response.file?.size),
    },
    matching: response.matching || null,
  };
}

export function normalizeSalesImportUploadError(error) {
  const payload = errorPayload(error);
  const networkFailure =
    error?.code === "ERR_NETWORK" ||
    Boolean(error?.request) ||
    /failed to fetch|networkerror|network request failed/i.test(
      String(error?.message || "")
    );
  const fallbackMessage =
    (networkFailure
      ? "No pudimos conectar con el servidor. Revisá tu conexión e intentá nuevamente."
      : error?.message) || "No se pudo verificar el archivo de ventas.";
  const rawErrors = Array.isArray(payload.errors) ? payload.errors : [];

  return {
    status: asNullableNumber(error?.status || error?.response?.status),
    code: String(payload.code || "UPLOAD_FAILED"),
    message: String(payload.message || fallbackMessage),
    conflictCount: asNumber(payload.conflictCount),
    integratedImportId: asNullableNumber(payload.integratedImportId),
    errors: rawErrors.map((item, index) => ({
      id: `${item?.code || "ERROR"}-${item?.row ?? "general"}-${index}`,
      row: asNullableNumber(item?.row),
      column: String(item?.column || ""),
      code: String(item?.code || payload.code || "UPLOAD_FAILED"),
      message: String(item?.message || payload.message || fallbackMessage),
      integratedImportId: asNullableNumber(item?.integratedImportId),
      receipt: item?.receipt || null,
    })),
  };
}

export function isBlockingSalesImportConflict(result) {
  return [
    "FILE_ALREADY_INTEGRATED",
    "CONTENT_ALREADY_INTEGRATED",
    "SALES_IMPORT_RECEIPT_OVERLAP",
  ].includes(result?.code);
}
